import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { WorkItem } from "../../types/workItems";
import { MDSAssessmentRow, MDSRowClickData } from "./MDSAssessmentRow";
import { DismissDialog } from "./DismissDialog";
import { ResidentData, SortField, SortDirection } from "../../types/dashboard";
import { STYLES } from "../../config/styles";
import { sortData } from "../../utils/dashboardUtils";
import { mapWorkItemToResidentData } from "../../utils/mdsMapping";
import { SortableHeader } from "../molecules/SortableHeader";
import { apiService } from "../../services/apiService";
import { useApiClient } from "../../contexts/ApiClientContext";
import { clearWorkItemCache } from "../../hooks/useWorkItemsAutoRefresh";
import { trackCareQueueEvent } from "../../lib/tracking";

interface MDSAssessmentsTableProps {
  workItems: WorkItem[];
  onRowClick: (data: MDSRowClickData) => void;
  canDismissWorkItems?: boolean;
  groupByFacility?: boolean;
  facilityNameMap?: Record<string, string>;
}

/*
// Local filter state (client-side only)
interface LocalFilters {
  status: string;
  residentName: string;
  location: string;
  admissionDate: string;
  mdsOcType: string;
  referenceWindow: string;
  ardDate: string;
  unsignedSections: string;
  completionDate: string;
  caaCompletion: string;
  carePlanCompletion: string;
  transmissionDate: string;
}*/

// Column configuration type
interface ColumnConfig {
  key: string;
  label: string;
  minWidth: string;
  sublabel?: string;
  sortField?: SortField; // Maps to ResidentData field for sorting
}

// Column configuration for MDS Assessments.
// NETSOL-12004:
//   - ARD (Assessment Reference Date) is second from the left (right after the
//     actions column) — item #3.
//   - Date of Birth, Payor, Location, Reference Window and Admission Date are no
//     longer columns; they now appear in a hover-over on each row (see
//     MDSAssessmentRow) — NETSOL-12004 item #2 + NETSOL-12075 (Admission Date).
const MDS_COLUMNS: ColumnConfig[] = [
  { key: "actions", label: "", minWidth: "min-w-[40px]" }, // Actions column (no sorting)
  { key: "ardDate", label: "Assessment Reference Date", sublabel: "(No later than)", minWidth: "min-w-[200px]", sortField: "ardDate" },
  { key: "status", label: "Assessment Status", minWidth: "min-w-[150px]", sortField: "status" },
  { key: "residentName", label: "Resident Name", minWidth: "min-w-[150px]", sortField: "residentName" },
  { key: "mdsOcType", label: "Assessment Type", minWidth: "min-w-[140px]", sortField: "mdsOcType" },
  { key: "unsignedSections", label: "Unsigned Sections", minWidth: "min-w-[150px]", sortField: "assignedServices" },
  { key: "completionDate", label: "Due Date", minWidth: "min-w-[130px]", sortField: "dueDate" },
  { key: "caaCompletion", label: "CAA Completion", minWidth: "min-w-[130px]", sortField: "caaCompletion" },
  { key: "carePlanCompletion", label: "Care Plan Completion", minWidth: "min-w-[130px]", sortField: "carePlanCompletionDate" },
  { key: "transmissionDate", label: "Transmission Date", minWidth: "min-w-[130px]", sortField: "transmissionDate" },
];

export function MDSAssessmentsTable({ workItems, onRowClick, canDismissWorkItems = false, groupByFacility = false, facilityNameMap = {} }: MDSAssessmentsTableProps) {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  // Sorting state - default to Due Date ascending
  const [sortField, setSortField] = useState<SortField | null>("dueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Collapsed facility groups
  const [collapsedFacilities, setCollapsedFacilities] = useState<Set<string>>(new Set());

  // Optimistic removal: track dismissed IDs to hide rows immediately
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Dismiss dialog state
  const [dismissTarget, setDismissTarget] = useState<ResidentData | null>(null);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: ({ workItemId, reason }: { workItemId: string; reason?: string }) => {
      if (apiClient) {
        return apiClient.dismissWorkItem(workItemId, reason);
      }
      return apiService.dismissWorkItem(workItemId, reason);
    },
    onSuccess: (_, { workItemId }) => {
      setDismissedIds((prev) => new Set(prev).add(workItemId));
      clearWorkItemCache("mds");
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "work-items-auto-refresh" &&
          query.queryKey[1] === "mds",
      });
      toast.success("Assessment removed from queue");
      setDismissDialogOpen(false);
      setDismissTarget(null);
    },
    onError: () => {
      toast.error("Failed to dismiss assessment");
    },
  });

  const handleDismiss = (row: ResidentData) => {
    setDismissTarget(row);
    setDismissDialogOpen(true);
  };

  const handleDismissConfirm = (reason?: string) => {
    if (dismissTarget?.work_item_id) {
      dismissMutation.mutate({ workItemId: dismissTarget.work_item_id, reason });
    }
  };

  // Handle sort column click
  const handleSort = (field: SortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      // Toggle direction if same field
      newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      // New field, default to ascending
      newDirection = "asc";
      setSortField(field);
      setSortDirection(newDirection);
    }
    trackCareQueueEvent("care_queue_sort_changed", {
      column: field,
      direction: newDirection,
      table: "mds",
    });
  };
  // Map work items to display data, filtering out optimistically dismissed items
  const mappedData = workItems
    .filter((item) => !dismissedIds.has(item.id))
    .map(mapWorkItemToResidentData);

  // Apply sorting
  const displayData = sortData(mappedData, sortField, sortDirection);

  return (
    <>
      <div className="border-2 border-solid border-border rounded-[var(--radius)] overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-auto flex-1">
          <table className={`w-full ${STYLES.container.minWidth}`}>
            {/* Table Header */}
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-border bg-muted">
                {MDS_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`${column.minWidth} p-3 text-center`}
                  >
                    {column.sortField ? (
                      <div className="flex items-center justify-center">
                        <SortableHeader
                          label={column.label}
                          sublabel={column.sublabel}
                          field={column.sortField}
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      </div>
                    ) : (
                      <>
                        <span>{column.label}</span>
                        {column.sublabel && <span className="italic"> {column.sublabel}</span>}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

          {/* Filter Row */}
          {/* <thead>
            <tr className="border-b border-border bg-card">
              {MDS_COLUMNS.map((column) => (
                <th key={`filter-${column.key}`} className="p-2">
                  <Input
                    type="text"
                    placeholder="Filter..."
                    value={localFilters[column.key as keyof LocalFilters] || ""}
                    onChange={(e) => handleLocalFilterChange(column.key as keyof LocalFilters, e.target.value)}
                    className="h-8"
                  />
                </th>
              ))}
            </tr>
          </thead> */}

          {/* Table Body */}
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td colSpan={MDS_COLUMNS.length} className="p-8 text-center text-muted-foreground">
                  No MDS Assessments found
                </td>
              </tr>
            ) : (
              (() => {
                if (!groupByFacility) {
                  return displayData.map((row, index) => (
                    <MDSAssessmentRow
                      key={row.work_item_id || index}
                      row={row}
                      index={index}
                      isLastRow={index === displayData.length - 1}
                      onClick={onRowClick}
                      onDismiss={handleDismiss}
                      canDismiss={canDismissWorkItems}
                    />
                  ));
                }
                // Group by facility
                const groups: { facilityId: string; facilityName: string; rows: ResidentData[] }[] = [];
                for (const row of displayData) {
                  const fid = row.facility_id || "unknown";
                  const fname = facilityNameMap[fid] || row.facility_name || fid || "Unknown Facility";
                  const existing = groups.find((g) => g.facilityId === fid);
                  if (existing) { existing.rows.push(row); }
                  else { groups.push({ facilityId: fid, facilityName: fname, rows: [row] }); }
                }
                return groups.flatMap(({ facilityId, facilityName, rows }) => {
                  const collapsed = collapsedFacilities.has(facilityId);
                  return [
                    <tr
                      key={`facility-${facilityId}`}
                      className="bg-muted/60 border-t-2 border-border cursor-pointer select-none hover:bg-muted/80"
                      onClick={() => setCollapsedFacilities(prev => {
                        const next = new Set(prev);
                        if (next.has(facilityId)) next.delete(facilityId); else next.add(facilityId);
                        return next;
                      })}
                    >
                      <td colSpan={MDS_COLUMNS.length} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        <span className="mr-2">{collapsed ? "▶" : "▼"}</span>
                        {facilityName} <span className="font-normal">({rows.length})</span>
                      </td>
                    </tr>,
                    ...rows.map((row, index) => (
                      <MDSAssessmentRow
                        key={row.work_item_id || `${facilityId}-${index}`}
                        row={row}
                        index={index}
                        isLastRow={!collapsed && index === rows.length - 1}
                        onClick={onRowClick}
                        onDismiss={handleDismiss}
                        canDismiss={canDismissWorkItems}
                        style={collapsed ? { display: 'none' } : undefined}
                      />
                    )),
                  ];
                });
              })()
            )}
          </tbody>
          </table>
        </div>
      </div>

      <DismissDialog
        open={dismissDialogOpen}
        onOpenChange={setDismissDialogOpen}
        onConfirm={handleDismissConfirm}
        isPending={dismissMutation.isPending}
      />
    </>
  );
}
